import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import useCheckDevice from "@repo/shopifyV2/hooks/useCheckDevice";
import type { Filter, FilterValue } from "@repo/shopifyV2/types/storefront.types";
import { getTotalProductCount } from "@repo/shopifyV2/utils/collections";

import Button from "@atoms/Button";
import Icon from "@atoms/Icon";

import FilterCard from "@molecules/Filter/FilterCard";
import Sort from "@molecules/Filter/Sort";

import useCheckLocation from "@hooks/useCheckLocation";
import useLanguage from "@hooks/useLanguage";

interface IProps {
  onClickClose: () => void;
  filters: Filter[];
  /** 필터 열림 시 배경 딤 처리 (검색 결과 페이지에서 사용) */
  setHasDim?: React.Dispatch<React.SetStateAction<boolean>>;
}

// 쿼리 파라미터 키 ↔ Shopify 필터 label. isVisible이 있으면 노출 대상만 추린다
const FILTER_GROUPS: { key: string; label: string; value: string; isVisible?: (value: FilterValue) => boolean }[] = [
  { key: "category", label: "Category", value: "category", isVisible: (value) => value.label !== "GWP" },
  { key: "size", label: "Size", value: "size" },
  { key: "color", label: "Color", value: "color", isVisible: (value) => value.id.includes("group") },
];

const ARCHIVE_FILTER_GROUPS: { key: string; label: string; value: string; isVisible?: (value: FilterValue) => boolean }[] = [
  { key: "tag", label: "Tag", value: "discount", isVisible: (value) => value.id.includes("archive_") },
  { key: "category", label: "Category", value: "category", isVisible: (value) => value.label !== "GWP" },
];

const ARCHIVE_DISCOUNT_FILTERS = [
  { count: 1, id: "filter.p.tag.archive_10", input: '{"tag":"Archive_10%"}', label: "Archive_10%" },
  { count: 1, id: "filter.p.tag.archive_20", input: '{"tag":"Archive_20%"}', label: "Archive_20%" },
  { count: 1, id: "filter.p.tag.archive_30", input: '{"tag":"Archive_30%"}', label: "Archive_30%" },
  { count: 1, id: "filter.p.tag.archive_40", input: '{"tag":"Archive_40%"}', label: "Archive_40%" },
  { count: 1, id: "filter.p.tag.archive_50", input: '{"tag":"Archive_50%"}', label: "Archive_50%" },
  { count: 1, id: "filter.p.tag.archive_60", input: '{"tag":"Archive_60%"}', label: "Archive_60%" },
];

// 선택된 필터는 ["A","B"] 형태의 JSON 문자열로 파라미터에 담긴다
const parseParam = (param: string | null): string[] => {
  if (!param) return [];

  try {
    return JSON.parse(param) as string[];
  } catch {
    return [];
  }
};

export default function Filter({ onClickClose, filters }: IProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const { isPC } = useCheckDevice();

  const { isArchive } = useCheckLocation();
  const { lang } = useLanguage();

  const filterGroups = useMemo(
    () =>
      (isArchive ? ARCHIVE_FILTER_GROUPS : FILTER_GROUPS).map(({ key, value, label, isVisible }) => ({
        key,
        value,
        values:
          isArchive && key === "tag"
            ? ARCHIVE_DISCOUNT_FILTERS.filter((item) => !(lang === "jp" && item.label === "Archive_60%"))
            : filters
                .filter((filter) => filter.label === label)
                .flatMap((filter) => filter.values)
                .filter((item) => isVisible?.(item) ?? true),
      })),
    [isArchive, filters],
  );

  const handleFilter = (option: FilterValue, key: string) => {
    const prevValues = parseParam(searchParams.get(key));
    const nextValues = prevValues.includes(option.label)
      ? prevValues.filter((value) => value !== option.label)
      : [...prevValues, option.label];

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);

      if (nextValues.length) next.set(key, JSON.stringify(nextValues));
      else next.delete(key);

      return next;
    });
  };

  const totalProducts = useMemo(() => getTotalProductCount(filters), [filters]);

  return (
    <>
      {isPC ? (
        <section className="flex h-fit w-full flex-col gap-10">
          {!isArchive && (
            <Button as="wrapper" className="items-center gap-2" onClick={onClickClose}>
              <Icon className="h-3.5 dark:text-white" name="LeftArrow" />
              <span>{`Hide Filter & Sort (${totalProducts})`}</span>
            </Button>
          )}
          <Sort />
          {filterGroups.map(({ key, values, value }) => (
            <FilterCard
              filterKey={value}
              filterList={values}
              key={key}
              onChangeFilter={handleFilter}
              selected={parseParam(searchParams.get(key))}
            />
          ))}
        </section>
      ) : (
        <section className="dark:bg-dj-black fixed inset-0 z-70 flex flex-col bg-white">
          <div className="tnt-gap-x grid grid-cols-8 items-center p-5">
            <Button as="wrapper" className="col-span-1" onClick={onClickClose}>
              <Icon className="h-3.5 dark:text-white" name="LeftArrow" />
            </Button>
            <Button as="wrapper" className="col-span-7" onClick={onClickClose}>
              <span>{`Hide Filter & Sort (${totalProducts})`}</span>
            </Button>
          </div>

          <div className="tnt-gap-x grid grid-cols-8 overflow-y-auto">
            <div className="col-span-7 col-start-2 flex flex-col gap-10 p-4 pt-0">
              <Sort />
              {filterGroups.map(({ key, values, value }) => (
                <FilterCard
                  filterKey={value}
                  filterList={values}
                  key={key}
                  onChangeFilter={handleFilter}
                  selected={parseParam(searchParams.get(key))}
                />
              ))}
            </div>
          </div>
          <div className="p-4 pt-0">
            <Button as="square" className="w-full" onClick={onClickClose}>
              Apply
            </Button>
          </div>
        </section>
      )}
    </>
  );
}
